import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useActiveFixturesSummary,
  useBatchPublishSuspensions,
  useBulkGateMarkets,
  useCreateBatchPublishSuspension,
  useGateMarket,
  useQuotesByFixture,
  useRecentlyEnteredMarkets,
  useRemoveBatchPublishSuspension,
  useSetMuteCompetitorSignals,
  useUngateMarket,
  queryKeys,
} from '../../api/hooks';
import { useAuth } from '../../auth/AuthContext';
import { useHomebaseModal } from '../../components/HomebaseModal';
import { parseProbMarginInput } from '../../utils/fixedMarginPreview';
import {
  batchSuspensionConfirmCopy,
  batchSuspensionFixtureLabel,
  batchPublishSuspensionForQuote,
  batchSuspensionFlagLabel,
} from '../../utils/batchSuspensionUtils';
import RecentlyEnteredPanel from './RecentlyEnteredPanel';
import ReseedPosteriorModal from './ReseedPosteriorModal';
import {
  deriveTileStatus,
  emptyTileDraft,
  filterActiveFixtures,
  filterRecentEntries,
  fixtureFullName,
  fixtureIsLive,
  fixtureStartDate,
  fixtureStatusLabel,
  batchSuspendButtonLabel,
  findActiveBatchSuspension,
  formatDecimalPrice,
  formatFairPct,
  formatFees,
  formatFixtureDateLabel,
  dateToInputValue,
  dateFromInputValue,
  formatMarginPp,
  formatZ2Innovation,
  marginPreviewDecimal,
  marginDraftPending,
  groupPendingDraftsForBulkGate,
  stagedFixedMarginEffective,
  quoteTrueMarginOver,
  quoteTrueMarginUnder,
  quotePostedDecimals,
  groupQuotesForBoard,
  isOneWay,
  needsShapePersistOverride,
  marketLabel,
  marketOptionsFromQuotes,
  BOARD_SORT_OPTIONS,
  leagueOptionsFromFixtures,
  normalizeActiveFixture,
  POPULARITY_FILTER_OPTIONS,
  quoteEntryCount,
  quoteHasManualOverride,
  quoteManualOverrideKinds,
  quoteTotalFees,
  quoteZ2Innovation,
  z2InnovationHeatStyle,
  SHARP_FEES_MIN,
  sportBucketForFixture,
  sportOptionsFromFixtures,
  startOfDay,
  tileIsPending,
} from './tradingDashUtils';
import './TradingDashOverview.css';

function StatusDot({ status }) {
  return <span className={`tdash-sdot tdash-sdot--${status}`} />;
}

function MarketTile({
  quote,
  draft,
  batchSuspension,
  onSetOneway,
  onSetMargin,
  onToggleMute,
  onToggleAlts,
  onRemoveOverride,
  onOpen,
  onReseed,
  actionBusy,
}) {
  const batchActive = Boolean(batchSuspension);
  const status = deriveTileStatus(quote, { batchSuspended: batchActive });
  const oneway = draft?.targetOneway ?? isOneWay(quote);
  const pending = tileIsPending(draft);
  const onewayPending = draft?.targetOneway !== draft?.committedOneway || Boolean(draft?.shapePersist);
  const marginPending = marginDraftPending(draft);
  const marginOverStr = draft?.targetMarginOver ?? '';
  const marginUnderStr = draft?.targetMarginUnder ?? '';
  const marginOverCommitted = draft?.committedMarginOver ?? '';
  const marginUnderCommitted = draft?.committedMarginUnder ?? '';
  const marginOverCustom = String(marginOverCommitted).trim() !== '';
  const marginUnderCustom = String(marginUnderCommitted).trim() !== '';
  const previewOverDec = marginPreviewDecimal(quote, 'over', marginOverStr, oneway);
  const previewUnderDec = marginPreviewDecimal(quote, 'under', marginUnderStr, oneway);
  const showOverPreview =
    marginPending
    && previewOverDec != null
    && marginOverStr.trim() !== ''
    && marginOverStr.trim() !== marginOverCommitted.trim();
  const showUnderPreview =
    marginPending
    && previewUnderDec != null
    && marginUnderStr.trim() !== ''
    && marginUnderStr.trim() !== marginUnderCommitted.trim()
    && !oneway;
  const effMarginOver =
    stagedFixedMarginEffective(quote, 'over', marginOverStr, oneway)
    ?? quoteTrueMarginOver(quote);
  const effMarginUnder =
    stagedFixedMarginEffective(quote, 'under', marginUnderStr, oneway)
    ?? quoteTrueMarginUnder(quote);
  const altsOn =
    quote.manual_alts_allow === true
    || (quote.manual_alts_allow == null
      && (quote.alt_margination_floor != null || quote.alt_margination_ceiling != null));
  const muteOn = Boolean(quote.mute_competitor_signals);
  const hasOverride = quoteHasManualOverride(quote);
  const overrideKinds = quoteManualOverrideKinds(quote);
  const posted = quotePostedDecimals(quote);
  const shapePersistEligible = needsShapePersistOverride(quote);
  const z2 = quoteZ2Innovation(quote);

  let flag = '';
  if (onewayPending) {
    if (draft?.shapePersist) {
      flag = draft?.targetOneway ? '→ apply 1w' : '→ apply 2w';
    } else {
      flag = draft?.targetOneway ? '→ 1w' : '→ 2w';
    }
  } else if (marginPending) {
    flag = '→ margin';
  } else if (batchActive) {
    flag = batchSuspensionFlagLabel(batchSuspension.scope);
  } else if (quote.manual_suspend) {
    flag = 'manual suspend';
  } else if (pending) {
    flag = 'edited';
  }

  const tileClass = [
    'tdash-tile',
    oneway ? 'tdash-tile--oneway' : '',
    batchActive ? 'tdash-tile--batch-suspended' : '',
    quote.manual_suspend ? 'tdash-tile--suspended' : '',
    !quote.gate_allowed ? 'tdash-tile--removed' : '',
    pending ? 'tdash-tile--edited' : '',
    hasOverride ? 'tdash-tile--override' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={tileClass}>
      {flag ? (
        <span
          className="tdash-tflag"
          title={batchActive ? `${batchSuspension.scopeLabel}: ${batchSuspension.reason}` : undefined}
        >
          {flag}
        </span>
      ) : null}
      {hasOverride ? (
        <div className="tdash-override-bar">
          <span className="tdash-override-label" title={overrideKinds.join(', ')}>
            Manual · {overrideKinds.join(' · ')}
          </span>
          <button
            type="button"
            className="tdash-override-clear"
            title="Clear suspend, shaping, mute, alts, and other manual settings"
            disabled={actionBusy}
            onClick={() => onRemoveOverride(quote.market_key)}
          >
            Remove override
          </button>
        </div>
      ) : null}
      <div className="tdash-trow1">
        <span className="tdash-lblwrap">
          <StatusDot status={status} />
          <span className="tdash-tline">{quote.line}</span>
          <span className="tdash-tstat">{marketLabel(quote.market_id)}</span>
          <span className="tdash-tfees" title={`${quoteEntryCount(quote)} entries`}>
            {formatFees(quoteTotalFees(quote))}
          </span>
          {z2 != null ? (
            <span
              className="tdash-tz2"
              style={z2InnovationHeatStyle(z2)}
              title="Book movement (innovation z²)"
            >
              z² {formatZ2Innovation(z2)}
            </span>
          ) : null}
        </span>
      </div>
      <div className="tdash-tquotes">
        <div className="tdash-tquote-line tdash-tquote-line--fair">
          <span className="tdash-tquote-lbl" title="Demarginated fair probability">fair</span>
          <span className="tdash-tprice tdash-tprice--fair">
            <span className="tdash-tprice-o">{formatFairPct(quote.p_fair)}</span>
            <span className="tdash-tprice-sl">/</span>
            <span className="tdash-tprice-u">{formatFairPct(quote.p_fair_under)}</span>
          </span>
        </div>
        <div className="tdash-tquote-line tdash-tquote-line--price">
          <span className="tdash-tquote-lbl" title="Marginated decimal odds at this line">price</span>
          <span className="tdash-tprice tdash-tprice--posted">
            <span className="tdash-tprice-o">
              {showOverPreview ? (
                <>
                  <span className="tdash-tprice-prev">{formatDecimalPrice(posted.over)}</span>
                  <span className="tdash-tprice-arrow">→</span>
                  <span className="tdash-tprice-next">{formatDecimalPrice(previewOverDec)}</span>
                </>
              ) : (
                formatDecimalPrice(posted.over)
              )}
            </span>
            <span className="tdash-tprice-sl">/</span>
            <span className="tdash-tprice-u">
              {showUnderPreview ? (
                <>
                  <span className="tdash-tprice-prev">{formatDecimalPrice(posted.under)}</span>
                  <span className="tdash-tprice-arrow">→</span>
                  <span className="tdash-tprice-next">{formatDecimalPrice(previewUnderDec)}</span>
                </>
              ) : (
                formatDecimalPrice(posted.under)
              )}
            </span>
          </span>
        </div>
        <div className="tdash-tquote-line tdash-tquote-line--margin">
          <span className="tdash-tquote-lbl" title="Fixed Δp override (probability vs fair); blank = auto logit">
            Δp
          </span>
          <div className="tdash-tmargin-grid">
            <label className="tdash-tmargin-side">
              <span className="tdash-tmarg-lbl">o</span>
              <span className="tdash-tmarg-pill" title="Effective over margin at posted price">
                {formatMarginPp(effMarginOver)}
              </span>
              <input
                type="text"
                inputMode="decimal"
                className={`tdash-tmargin-input${marginOverCustom ? ' custom' : ''}${marginPending && marginOverStr.trim() !== marginOverCommitted.trim() ? ' pending' : ''}`}
                placeholder="auto"
                value={marginOverStr}
                onChange={(e) => onSetMargin(quote.market_key, 'over', e.target.value)}
                aria-label="Fixed over margin delta p"
                title="Fixed over margin vs fair (e.g. 0.06). Clamped to main Δp floor/ceiling. Blank clears on confirm."
              />
            </label>
            <label className={`tdash-tmargin-side${oneway ? ' tdash-tmargin-side--off' : ''}`}>
              <span className="tdash-tmarg-lbl">u</span>
              <span className="tdash-tmarg-pill" title="Effective under margin at posted price">
                {oneway ? '—' : formatMarginPp(effMarginUnder)}
              </span>
              <input
                type="text"
                inputMode="decimal"
                className={`tdash-tmargin-input${marginUnderCustom ? ' custom' : ''}${marginPending && marginUnderStr.trim() !== marginUnderCommitted.trim() ? ' pending' : ''}`}
                placeholder="auto"
                value={marginUnderStr}
                disabled={oneway}
                onChange={(e) => onSetMargin(quote.market_key, 'under', e.target.value)}
                aria-label="Fixed under margin delta p"
                title="Fixed under margin vs fair (e.g. 0.06). Clamped to main Δp floor/ceiling. Blank clears on confirm."
              />
            </label>
          </div>
        </div>
      </div>
      <div className="tdash-tactions">
        <button
          type="button"
          className={`tdash-act${oneway ? ' on' : ''}${onewayPending && oneway ? ' pending' : ''}`}
          title={
            shapePersistEligible && oneway
              ? 'Apply one-sided override and reopen (leave-open shaping)'
              : 'Stage one-way'
          }
          onClick={() => onSetOneway(quote.market_key, true)}
        >
          1w
        </button>
        <button
          type="button"
          className={`tdash-act${!oneway ? ' on' : ''}${onewayPending && !oneway ? ' pending' : ''}`}
          title={
            shapePersistEligible && !oneway
              ? 'Apply two-sided override and reopen (leave-open shaping)'
              : 'Stage two-way'
          }
          onClick={() => onSetOneway(quote.market_key, false)}
        >
          2w
        </button>
        <button
          type="button"
          className={`tdash-act${altsOn ? ' on' : ''}`}
          title="Toggle serve alt lines"
          disabled={actionBusy}
          onClick={() => onToggleAlts(quote)}
        >
          Alts
        </button>
        <button
          type="button"
          className={`tdash-act${muteOn ? ' on' : ''}`}
          title="Mute competitor signals"
          disabled={actionBusy}
          onClick={() => onToggleMute(quote)}
        >
          Mute
        </button>
        <button
          type="button"
          className="tdash-act"
          title="Hard-reseed baseline line and posterior (x, P)"
          disabled={actionBusy}
          onClick={() => onReseed(quote)}
        >
          Reseed
        </button>
        <button
          type="button"
          className="tdash-act tdash-act--link"
          title="Open market dashboard"
          onClick={() => onOpen(quote.market_key)}
        >
          More ›
        </button>
      </div>
    </div>
  );
}

export default function TradingDashOverview() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { confirm, alert, ModalLayer } = useHomebaseModal();
  const gatedBy = user?.email?.trim() || 'trading-dash';

  const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()));
  const [sport, setSport] = useState('all');
  const [league, setLeague] = useState('all');
  const [playerFilter, setPlayerFilter] = useState('');
  const [marketTypeFilter, setMarketTypeFilter] = useState('all');
  const [popularityFilter, setPopularityFilter] = useState('all');
  const [boardSort, setBoardSort] = useState('player');
  const [includeInGame, setIncludeInGame] = useState(false);
  const [sharpFeesOnly, setSharpFeesOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedFixtureId, setSelectedFixtureId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [reseedMarketKey, setReseedMarketKey] = useState(null);

  const { data: fixturesPayload, isLoading: scheduleLoading } = useActiveFixturesSummary();
  const allFixtures = useMemo(
    () => fixturesPayload?.fixtures || [],
    [fixturesPayload?.fixtures]
  );
  const sportOptions = useMemo(
    () => sportOptionsFromFixtures(allFixtures.map((f) => normalizeActiveFixture(f)).filter(Boolean)),
    [allFixtures]
  );
  const leagueOptions = useMemo(() => leagueOptionsFromFixtures(allFixtures), [allFixtures]);

  const fixtures = useMemo(
    () => filterActiveFixtures(allFixtures, { selectedDate, sport, league, search, includeInGame }),
    [allFixtures, selectedDate, sport, league, search, includeInGame]
  );

  const liveFixtureCount = useMemo(
    () =>
      filterActiveFixtures(allFixtures, { selectedDate, sport, league, search, includeInGame: true }).filter(
        fixtureIsLive
      ).length,
    [allFixtures, selectedDate, sport, league, search]
  );

  useEffect(() => {
    if (!fixtures.length) {
      setSelectedFixtureId(null);
      return;
    }
    if (!selectedFixtureId || !fixtures.some((f) => f.game_id === selectedFixtureId)) {
      setSelectedFixtureId(fixtures[0].game_id);
    }
  }, [fixtures, selectedFixtureId]);

  const selectedFixture = useMemo(
    () => fixtures.find((f) => f.game_id === selectedFixtureId) || null,
    [fixtures, selectedFixtureId]
  );

  const { data: quotes = [], isLoading: quotesLoading } = useQuotesByFixture(selectedFixtureId);
  const { data: batchSuspPayload } = useBatchPublishSuspensions(true, null);
  const batchSuspensions = useMemo(
    () => (Array.isArray(batchSuspPayload?.suspensions) ? batchSuspPayload.suspensions : []),
    [batchSuspPayload]
  );
  const createBatchSuspension = useCreateBatchPublishSuspension();
  const removeBatchSuspension = useRemoveBatchPublishSuspension();
  const batchToggleBusy = createBatchSuspension.isPending || removeBatchSuspension.isPending;
  const { data: recentEntriesRaw = [], isLoading: recentEntriesLoading } = useRecentlyEnteredMarkets({
    minutes: 60,
    limit: 120,
  });

  const filteredRecentEntries = useMemo(
    () =>
      filterRecentEntries(recentEntriesRaw, {
        league,
        player: playerFilter,
      }),
    [recentEntriesRaw, league, playerFilter]
  );

  const boardTeams = useMemo(
    () =>
      groupQuotesForBoard(quotes, selectedFixture, {
        search,
        player: playerFilter,
        marketType: marketTypeFilter,
        popularity: popularityFilter,
        sortBy: boardSort,
      }),
    [quotes, selectedFixture, search, playerFilter, marketTypeFilter, popularityFilter, boardSort]
  );

  const marketTypeOptions = useMemo(() => marketOptionsFromQuotes(quotes), [quotes]);

  const playerOptions = useMemo(() => {
    const names = new Set();
    for (const q of quotes) {
      if (q.player_name) names.add(q.player_name);
    }
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [quotes]);

  const gateMarket = useGateMarket();
  const bulkGateMarkets = useBulkGateMarkets();
  const ungateMarket = useUngateMarket();
  const setMuteCompetitor = useSetMuteCompetitorSignals();
  const tileActionBusy =
    gateMarket.isPending
    || bulkGateMarkets.isPending
    || ungateMarket.isPending
    || setMuteCompetitor.isPending
    || batchToggleBusy;

  const marketCounts = useMemo(() => {
    const m = {};
    for (const f of allFixtures) {
      const id = f.fixture_id || f.game_id;
      if (id) m[id] = f.total_engines || 0;
    }
    return m;
  }, [allFixtures]);

  const syncDraftsFromQuotes = useCallback((rows) => {
    setDrafts((prev) => {
      const next = { ...prev };
      const keys = new Set(rows.map((q) => q.market_key));
      for (const key of Object.keys(next)) {
        if (!keys.has(key)) delete next[key];
      }
      for (const q of rows) {
        const mk = q.market_key;
        const fresh = emptyTileDraft(q);
        if (!next[mk]) {
          next[mk] = fresh;
          continue;
        }
        const cur = next[mk];
        const serverCommittedChanged =
          cur.committedOneway !== fresh.committedOneway
          || cur.committedMarginOver !== fresh.committedMarginOver
          || cur.committedMarginUnder !== fresh.committedMarginUnder;
        // Reset staged UI when server state changes (e.g. remove override / external ungate).
        if (!tileIsPending(cur) || serverCommittedChanged) {
          next[mk] = fresh;
        }
      }
      return next;
    });
  }, []);

  const resetTileDraft = useCallback(
    (marketKey, rowOverride) => {
      setDrafts((prev) => {
        const quote = rowOverride || quotes.find((q) => q.market_key === marketKey);
        if (!quote) {
          if (!(marketKey in prev)) return prev;
          const next = { ...prev };
          delete next[marketKey];
          return next;
        }
        return { ...prev, [marketKey]: emptyTileDraft(quote) };
      });
    },
    [quotes]
  );

  const handleBatchToggle = useCallback(
    async ({ scope, fixture, playerId, teamSide, playerName, teamName }) => {
      if (!fixture?.game_id) return;
      const sportBucket = sportBucketForFixture(fixture);
      const fixtureId = fixture.game_id;
      const existing = findActiveBatchSuspension(batchSuspensions, {
        sportBucket,
        scope,
        fixtureId,
        playerId,
        teamSide,
      });
      const copy = batchSuspensionConfirmCopy({
        clearing: Boolean(existing?.id),
        scope,
        fixtureLabel: batchSuspensionFixtureLabel(fixture) || fixtureFullName(fixture),
        playerName,
        teamName,
        teamSide,
      });
      const ok = await confirm({
        title: copy.title,
        body: copy.body,
        confirmLabel: copy.confirmLabel,
        danger: copy.danger,
      });
      if (!ok) return;
      try {
        if (existing?.id) {
          await removeBatchSuspension.mutateAsync(existing.id);
        } else {
          await createBatchSuspension.mutateAsync({
            sport_bucket: sportBucket,
            scope,
            fixture_id: fixtureId,
            player_id: playerId,
            team_side: teamSide,
            reason: 'Trading dash batch suspend',
            created_by: gatedBy,
            fanout_writers: true,
          });
        }
      } catch (err) {
        console.error('Batch suspend toggle failed:', err);
        await alert({
          title: 'Batch suspension failed',
          body: err?.response?.data?.detail || err?.message || 'Could not update batch suspension.',
          warn: true,
        });
      }
    },
    [batchSuspensions, createBatchSuspension, removeBatchSuspension, gatedBy, confirm, alert]
  );

  useEffect(() => {
    syncDraftsFromQuotes(quotes);
  }, [quotes, syncDraftsFromQuotes]);

  const pendingCount = useMemo(
    () => Object.values(drafts).filter(tileIsPending).length,
    [drafts]
  );

  const handleOpenMarket = useCallback(
    (marketKey) => {
      navigate(`/dashboard?market=${encodeURIComponent(marketKey)}`);
    },
    [navigate]
  );

  // Track the key, not the quote object, so the modal keeps following live quote refetches.
  const handleReseedMarket = useCallback((quote) => {
    setReseedMarketKey(quote?.market_key || null);
  }, []);
  const closeReseedModal = useCallback(() => setReseedMarketKey(null), []);
  const reseedQuote = useMemo(
    () => (reseedMarketKey ? quotes.find((q) => q.market_key === reseedMarketKey) || null : null),
    [quotes, reseedMarketKey]
  );

  const gameBatchActive = useMemo(() => {
    if (!selectedFixture) return null;
    return findActiveBatchSuspension(batchSuspensions, {
      sportBucket: sportBucketForFixture(selectedFixture),
      scope: 'fixture',
      fixtureId: selectedFixture.game_id,
    });
  }, [batchSuspensions, selectedFixture]);

  const discardAll = useCallback(() => {
    const next = {};
    for (const q of quotes) next[q.market_key] = emptyTileDraft(q);
    setDrafts(next);
  }, [quotes]);

  const setTileOneway = useCallback((marketKey, oneway) => {
    setDrafts((prev) => {
      const cur = prev[marketKey];
      if (!cur) return prev;
      const quote = quotes.find((q) => q.market_key === marketKey);
      let shapePersist = false;
      if (quote && needsShapePersistOverride(quote)) {
        const naturalOneWay = isOneWay(quote);
        if (oneway === naturalOneWay && oneway === cur.committedOneway) {
          shapePersist = true;
        }
      }
      return {
        ...prev,
        [marketKey]: {
          ...cur,
          targetOneway: oneway,
          shapePersist,
        },
      };
    });
  }, [quotes]);

  const setTileMargin = useCallback((marketKey, side, value) => {
    setDrafts((prev) => {
      const cur = prev[marketKey];
      if (!cur) return prev;
      if (side === 'under') {
        return { ...prev, [marketKey]: { ...cur, targetMarginUnder: value } };
      }
      return { ...prev, [marketKey]: { ...cur, targetMarginOver: value } };
    });
  }, []);

  const refreshFixtureQuotes = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['quotesByFixture', selectedFixtureId || ''] });
  }, [queryClient, selectedFixtureId]);

  const handleToggleMute = useCallback(
    async (quote) => {
      const mk = quote.market_key;
      const next = !quote.mute_competitor_signals;
      const ok = await confirm({
        title: next ? 'Mute competitor' : 'Resume competitor',
        body: next
          ? 'Stop base-KF → EntriesKF z² sync and z²-gated synthetic OB ladder refresh.'
          : 'Restore base-KF competitor sync and z²-gated OB refresh.',
        confirmLabel: next ? 'Mute' : 'Resume',
      });
      if (!ok) return;
      try {
        await setMuteCompetitor.mutateAsync({ marketKey: mk, enabled: next });
        await refreshFixtureQuotes();
      } catch (err) {
        console.error('Failed to toggle competitor mute:', err);
        await alert({
          title: 'Request failed',
          body: err?.response?.data?.detail || err?.message || 'Could not update mute setting.',
          warn: true,
        });
      }
    },
    [setMuteCompetitor, refreshFixtureQuotes, confirm, alert]
  );

  const handleToggleAlts = useCallback(
    async (quote) => {
      const mk = quote.market_key;
      const altsOn =
        quote.manual_alts_allow === true
        || (quote.manual_alts_allow == null
          && (quote.alt_margination_floor != null || quote.alt_margination_ceiling != null));
      try {
        await gateMarket.mutateAsync({
          marketKey: mk,
          gatedBy,
          leave_open: true,
          manual_alts_allow: altsOn ? false : true,
        });
        await refreshFixtureQuotes();
      } catch (err) {
        console.error('Failed to toggle alts:', err);
      }
    },
    [gateMarket, gatedBy, refreshFixtureQuotes]
  );

  const handleRemoveOverride = useCallback(
    async (marketKey) => {
      const ok = await confirm({
        title: 'Remove manual override',
        body:
          'Clears suspend, shaping, competitor mute, alts policy, and other manual settings for this market.',
        danger: true,
        confirmLabel: 'Remove',
      });
      if (!ok) return;
      try {
        await ungateMarket.mutateAsync(marketKey);
        if (selectedFixtureId) {
          await queryClient.refetchQueries({
            queryKey: queryKeys.quotesByFixture(selectedFixtureId),
          });
        }
        const freshRows =
          queryClient.getQueryData(queryKeys.quotesByFixture(selectedFixtureId)) || quotes;
        const freshQuote = freshRows.find((q) => q.market_key === marketKey);
        resetTileDraft(marketKey, freshQuote);
      } catch (err) {
        console.error('Failed to remove override:', err);
        await alert({
          title: 'Request failed',
          body: err?.response?.data?.detail || err?.message || 'Could not remove override.',
          warn: true,
        });
      }
    },
    [ungateMarket, queryClient, selectedFixtureId, quotes, resetTileDraft, confirm, alert]
  );

  const confirmAll = useCallback(async () => {
    const pending = Object.entries(drafts).filter(([, d]) => tileIsPending(d));
    if (!pending.length) return;

    for (const [, draft] of pending) {
      if (!marginDraftPending(draft)) continue;
      try {
        if (String(draft.targetMarginOver ?? '').trim() !== String(draft.committedMarginOver ?? '').trim()) {
          parseProbMarginInput(draft.targetMarginOver, 'Over margin');
        }
        if (String(draft.targetMarginUnder ?? '').trim() !== String(draft.committedMarginUnder ?? '').trim()) {
          parseProbMarginInput(draft.targetMarginUnder, 'Under margin');
        }
      } catch (err) {
        await alert({
          title: 'Fixed margin',
          body: err?.message || 'Invalid margin value.',
          warn: true,
        });
        return;
      }
    }

    setConfirmBusy(true);
    try {
      const batches = groupPendingDraftsForBulkGate(pending);
      const failed = [];
      for (const batch of batches) {
        const body = {
          market_keys: batch.market_keys,
          gated_by: gatedBy,
          fixture_id: selectedFixtureId || undefined,
          fanout_writers: true,
          ...batch.payload,
        };
        try {
          const res = await bulkGateMarkets.mutateAsync(body);
          for (const item of res?.results || []) {
            if (!item.ok) {
              failed.push({
                marketKey: item.market_key,
                detail: item.detail || 'failed',
              });
            }
          }
          if ((res?.failed || 0) > 0 && !(res?.results || []).length) {
            failed.push({ marketKey: 'batch', detail: 'Bulk gate reported failures' });
          }
        } catch (err) {
          const detail = err?.response?.data?.detail || err?.message || 'Bulk gate request failed';
          for (const mk of batch.market_keys) {
            failed.push({ marketKey: mk, detail });
          }
        }
      }

      if (selectedFixtureId) {
        await queryClient.refetchQueries({
          queryKey: queryKeys.quotesByFixture(selectedFixtureId),
        });
        const fresh =
          queryClient.getQueryData(queryKeys.quotesByFixture(selectedFixtureId)) || [];
        syncDraftsFromQuotes(fresh);
      }

      if (failed.length) {
        const preview = failed
          .slice(0, 5)
          .map((f) => `${f.marketKey}: ${f.detail}`)
          .join('\n');
        await alert({
          title: 'Some markets failed',
          body:
            failed.length > 5
              ? `${preview}\n…and ${failed.length - 5} more`
              : preview,
          warn: true,
        });
      }
    } finally {
      setConfirmBusy(false);
    }
  }, [
    drafts,
    bulkGateMarkets,
    gatedBy,
    queryClient,
    selectedFixtureId,
    syncDraftsFromQuotes,
    alert,
  ]);

  const shiftDate = (days) => {
    setSelectedDate((d) => {
      const n = new Date(d);
      n.setDate(n.getDate() + days);
      return startOfDay(n);
    });
  };

  return (
    <div className="tdash-root">
      <header className="tdash-top">
        <div className="tdash-brand">
          Big <span>Board</span> · Trading Dash
        </div>
        <div className="tdash-seg">
          <button
            type="button"
            className={sport === 'all' ? 'on' : ''}
            onClick={() => setSport('all')}
          >
            All
          </button>
          {sportOptions.map((s) => (
            <button
              key={s}
              type="button"
              className={sport === s ? 'on' : ''}
              onClick={() => setSport(s)}
            >
              {s.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="tdash-pre-pill">
          <span className={`tdash-dot ${includeInGame ? 'tdash-dot--live' : 'tdash-dot--soon'}`} />
          {includeInGame ? 'ALL GAMES' : 'PRE-GAME ONLY'} · <strong>{fixtures.length}</strong> fixtures
          {liveFixtureCount > 0 ? (
            <>
              {' '}
              · <strong>{liveFixtureCount}</strong> live
            </>
          ) : null}
        </div>
        <div className="tdash-spacer" />
        <div className="tdash-search">
          <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" aria-hidden>
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4-4" />
          </svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search player or team…"
          />
        </div>
        <div className="tdash-dater">
          <button type="button" onClick={() => shiftDate(-1)}>
            ← prev
          </button>
          <label className="tdash-dater-pick" title="Jump to date">
            <span className="tdash-dater-pick-ico" aria-hidden>
              📅
            </span>
            <input
              type="date"
              className="tdash-dater-input"
              value={dateToInputValue(selectedDate)}
              onChange={(e) => {
                const next = dateFromInputValue(e.target.value);
                if (next) setSelectedDate(next);
              }}
            />
          </label>
          <button type="button" onClick={() => shiftDate(1)}>
            next →
          </button>
          <button
            type="button"
            className="today"
            onClick={() => setSelectedDate(startOfDay(new Date()))}
          >
            today
          </button>
          <span className="tdash-datelabel">{formatFixtureDateLabel(selectedDate)}</span>
        </div>
      </header>

      <div className="tdash-filters">
        <label className="tdash-filter tdash-filter-toggle">
          <span className="tdash-filter-label">In-game</span>
          <button
            type="button"
            className={`tdash-toggle${includeInGame ? ' on' : ''}`}
            onClick={() => setIncludeInGame((v) => !v)}
            aria-pressed={includeInGame}
          >
            <span className="tdash-toggle-knob" />
            <span className="tdash-toggle-text">{includeInGame ? 'Included' : 'Excluded'}</span>
          </button>
        </label>
        <label className="tdash-filter">
          <span className="tdash-filter-label">League</span>
          <select value={league} onChange={(e) => setLeague(e.target.value)}>
            <option value="all">All leagues</option>
            {leagueOptions.map((lg) => (
              <option key={lg} value={lg}>
                {lg}
              </option>
            ))}
          </select>
        </label>
        <label className="tdash-filter">
          <span className="tdash-filter-label">Player</span>
          <select value={playerFilter} onChange={(e) => setPlayerFilter(e.target.value)}>
            <option value="">All players</option>
            {playerOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label className="tdash-filter">
          <span className="tdash-filter-label">Market type</span>
          <select value={marketTypeFilter} onChange={(e) => setMarketTypeFilter(e.target.value)}>
            <option value="all">All markets</option>
            {marketTypeOptions.map((mid) => (
              <option key={mid} value={mid}>
                {marketLabel(mid)}
              </option>
            ))}
          </select>
        </label>
        <label className="tdash-filter">
          <span className="tdash-filter-label">Popularity</span>
          <select value={popularityFilter} onChange={(e) => setPopularityFilter(e.target.value)}>
            {POPULARITY_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="tdash-filter tdash-filter-toggle">
          <span className="tdash-filter-label">Sharp fees</span>
          <button
            type="button"
            className={`tdash-toggle${sharpFeesOnly ? ' on' : ''}`}
            onClick={() => setSharpFeesOnly((v) => !v)}
            aria-pressed={sharpFeesOnly}
            title={`Fee-weighted sharp ≥ ${SHARP_FEES_MIN}`}
          >
            <span className="tdash-toggle-knob" />
            <span className="tdash-toggle-text">{sharpFeesOnly ? 'Sharp only' : 'All fees'}</span>
          </button>
        </label>
        <label className="tdash-filter">
          <span className="tdash-filter-label">Sort by</span>
          <select value={boardSort} onChange={(e) => setBoardSort(e.target.value)}>
            {BOARD_SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        {(league !== 'all'
          || playerFilter
          || marketTypeFilter !== 'all'
          || popularityFilter !== 'all'
          || sharpFeesOnly) && (
          <button
            type="button"
            className="tdash-btn tdash-filter-clear"
            onClick={() => {
              setLeague('all');
              setPlayerFilter('');
              setMarketTypeFilter('all');
              setPopularityFilter('all');
              setSharpFeesOnly(false);
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="tdash-body">
        <aside className="tdash-rail">
          <h4>
            All games · {fixtures.length}
            {scheduleLoading ? ' …' : ''}
          </h4>
          {fixtures.length === 0 ? (
            <div className="tdash-rail-empty">No fixtures on this date.</div>
          ) : (
            fixtures.map((fixture) => {
              const live = fixtureIsLive(fixture);
              const count = marketCounts[fixture.game_id];
              const fixtureBatchActive = findActiveBatchSuspension(batchSuspensions, {
                sportBucket: sportBucketForFixture(fixture),
                scope: 'fixture',
                fixtureId: fixture.game_id,
              });
              return (
                <div
                  key={fixture.game_id}
                  className={[
                    'tdash-game',
                    fixture.game_id === selectedFixtureId ? 'on' : '',
                    fixtureBatchActive ? 'tdash-game--batch-suspended' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setSelectedFixtureId(fixture.game_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedFixtureId(fixture.game_id);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="tdash-gcol">
                    <div className="tdash-mt">
                      <span className={`tdash-dot ${live ? 'tdash-dot--live' : 'tdash-dot--soon'}`} />
                      <span className="tdash-fixname">{fixtureFullName(fixture)}</span>
                    </div>
                    <div className="tdash-sub">
                      {count != null ? `${count} markets · ` : ''}
                      {fixtureStatusLabel(fixture)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`tdash-railsusp${fixtureBatchActive ? ' on' : ''}`}
                    title={
                      fixtureBatchActive
                        ? 'Clear batch suspend game'
                        : 'Batch suspend game'
                    }
                    disabled={batchToggleBusy}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBatchToggle({ scope: 'fixture', fixture });
                    }}
                  >
                    ⏸
                  </button>
                </div>
              );
            })
          )}
        </aside>

        <section className="tdash-main">
          <div className={`tdash-pending${pendingCount > 0 ? ' show' : ''}`}>
            <div className="tdash-pc">
              <b>{pendingCount}</b> pending change{pendingCount === 1 ? '' : 's'}
            </div>
            <div className="tdash-pdesc">edits save here — nothing changes until you confirm</div>
            <div className="tdash-pbtns">
              <button type="button" className="tdash-btn" onClick={discardAll} disabled={confirmBusy}>
                Discard
              </button>
              <button
                type="button"
                className="tdash-btn tdash-btn--primary"
                onClick={confirmAll}
                disabled={confirmBusy || pendingCount === 0}
              >
                {confirmBusy ? 'Pushing…' : 'Confirm & push'}
              </button>
            </div>
          </div>

          <div className="tdash-content">
            {selectedFixture ? (
              <>
                <div className="tdash-fixture-head">
                  <div className="tdash-gamehdr">
                    <span className="tdash-gn">{fixtureFullName(selectedFixture)}</span>
                    <span className="tdash-gmeta">
                      {formatFixtureDateLabel(fixtureStartDate(selectedFixture))} ·{' '}
                      {fixtureIsLive(selectedFixture) ? 'live' : 'pre-game'} ·{' '}
                      {quotes.length} markets
                    </span>
                    <button
                      type="button"
                      className={`tdash-susp-btn${gameBatchActive ? ' tdash-susp-btn--armed' : ''}`}
                      title={
                        gameBatchActive
                          ? 'Clear batch suspend for full game (publish overlay only)'
                          : 'Batch suspend full game (publish overlay only; engine unchanged)'
                      }
                      disabled={batchToggleBusy}
                      onClick={() => handleBatchToggle({ scope: 'fixture', fixture: selectedFixture })}
                    >
                      {batchSuspendButtonLabel('full game', Boolean(gameBatchActive))}
                    </button>
                  </div>

                  <div className="tdash-legend">
                    <span>
                      <StatusDot status="green" /> allowed · quoting
                    </span>
                    <span>
                      <StatusDot status="grey" /> limited / shaping
                    </span>
                    <span>
                      <StatusDot status="batch" /> batch suspended (payload)
                    </span>
                    <span>
                      <StatusDot status="yellow" /> manual suspend
                    </span>
                    <span>
                      <StatusDot status="red" /> blocked
                    </span>
                  </div>
                </div>

                {gameBatchActive ? (
                  <div className="tdash-batch-banner" title={gameBatchActive.reason}>
                    Batch suspended · full game — markets greyed below (payload only; engine still quoting)
                  </div>
                ) : null}

                <div className="tdash-workspace">
                  <div className="tdash-board-col">
                    <div className="tdash-board-scroll">
                      {quotesLoading && !quotes.length ? (
                        <div className="tdash-loading">Loading markets…</div>
                      ) : boardTeams.length === 0 ? (
                        <div className="tdash-empty">No markets match your filters.</div>
                      ) : (
                        <div className="tdash-board">
                          {boardTeams.map((team) => {
                            const isGameLines = team.side === 'game_lines';
                            const teamBatchActive = selectedFixture && team.side !== 'unknown' && !isGameLines
                              ? findActiveBatchSuspension(batchSuspensions, {
                                  sportBucket: sportBucketForFixture(selectedFixture),
                                  scope: 'fixture_team',
                                  fixtureId: selectedFixture.game_id,
                                  teamSide: team.side,
                                })
                              : null;
                            return (
                              <div
                                className={`tdash-teamblock${teamBatchActive ? ' tdash-teamblock--batch-suspended' : ''}${team.side === 'unknown' ? ' tdash-teamblock--unknown' : ''}${isGameLines ? ' tdash-teamblock--game-lines' : ''}`}
                                key={isGameLines || team.side === 'unknown' ? team.side : team.code}
                              >
                                <div className="tdash-teamhdr">
                                  <span className="tdash-tc">{team.code}</span>
                                  <span className="tdash-tn">{team.name}</span>
                                  {team.side !== 'unknown' && !isGameLines ? (
                                    <span className="tdash-side">{team.side}</span>
                                  ) : null}
                                  {teamBatchActive ? (
                                    <span className="tdash-batch-scope-tag" title={teamBatchActive.reason}>
                                      {batchSuspensionFlagLabel('fixture_team')}
                                    </span>
                                  ) : null}
                                  {team.side !== 'unknown' && !isGameLines ? (
                                    <button
                                      type="button"
                                      className={`tdash-susp-btn${teamBatchActive ? ' tdash-susp-btn--armed' : ''}`}
                                      title={
                                        teamBatchActive
                                          ? 'Clear batch suspend for this team'
                                          : 'Batch suspend all markets for this team'
                                      }
                                      disabled={batchToggleBusy}
                                      onClick={() =>
                                        handleBatchToggle({
                                          scope: 'fixture_team',
                                          fixture: selectedFixture,
                                          teamSide: team.side,
                                          teamName: team.name,
                                        })
                                      }
                                    >
                                      {batchSuspendButtonLabel('team', Boolean(teamBatchActive))}
                                    </button>
                                  ) : null}
                                </div>
                                {team.players.length === 0 ? (
                                  <div className="tdash-team-empty">No markets for this team.</div>
                                ) : null}
                                {team.players.map((player) => {
                                  const playerBatchActive = selectedFixture
                                    ? findActiveBatchSuspension(batchSuspensions, {
                                        sportBucket: sportBucketForFixture(selectedFixture),
                                        scope: 'fixture_player',
                                        fixtureId: selectedFixture.game_id,
                                        playerId: player.id,
                                      })
                                    : null;
                                  return (
                                    <div
                                      className={`tdash-playerblock${playerBatchActive ? ' tdash-playerblock--batch-suspended' : ''}`}
                                      key={player.id}
                                    >
                                      <div className="tdash-phead">
                                        <span className="tdash-pn">{player.name}</span>
                                        <span className="tdash-pp">{player.pos}</span>
                                        {playerBatchActive ? (
                                          <span
                                            className="tdash-batch-scope-tag"
                                            title={playerBatchActive.reason}
                                          >
                                            {batchSuspensionFlagLabel('fixture_player')}
                                          </span>
                                        ) : null}
                                        <span className="tdash-pfees" title={`${player.totalEntries} entries`}>
                                          {formatFees(player.totalFees)}
                                        </span>
                                        <button
                                          type="button"
                                          className={`tdash-susp-btn${playerBatchActive ? ' tdash-susp-btn--armed' : ''}`}
                                          title={
                                            playerBatchActive
                                              ? 'Clear batch suspend for this player'
                                              : 'Batch suspend all markets for this player'
                                          }
                                          disabled={batchToggleBusy}
                                          onClick={() =>
                                            handleBatchToggle({
                                              scope: 'fixture_player',
                                              fixture: selectedFixture,
                                              playerId: player.id,
                                              playerName: player.name,
                                            })
                                          }
                                        >
                                          {batchSuspendButtonLabel('player', Boolean(playerBatchActive))}
                                        </button>
                                      </div>
                                      <div className="tdash-tiles">
                                        {player.tiles.map((quote) => (
                                          <MarketTile
                                            key={quote.market_key}
                                            quote={quote}
                                            draft={drafts[quote.market_key] ?? emptyTileDraft(quote)}
                                            batchSuspension={batchPublishSuspensionForQuote(
                                              quote,
                                              batchSuspensions,
                                              selectedFixture
                                            )}
                                            onSetOneway={setTileOneway}
                                            onSetMargin={setTileMargin}
                                            onToggleMute={handleToggleMute}
                                            onToggleAlts={handleToggleAlts}
                                            onRemoveOverride={handleRemoveOverride}
                                            onOpen={handleOpenMarket}
                                            onReseed={handleReseedMarket}
                                            actionBusy={tileActionBusy}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  <RecentlyEnteredPanel
                    rows={filteredRecentEntries}
                    isLoading={recentEntriesLoading}
                    minutes={60}
                    compact
                  />
                </div>
              </>
            ) : (
              <div className="tdash-workspace">
                <div className="tdash-board-col">
                  <div className="tdash-empty">Select a fixture from the rail.</div>
                </div>
                <RecentlyEnteredPanel
                  rows={filteredRecentEntries}
                  isLoading={recentEntriesLoading}
                  minutes={60}
                  compact
                />
              </div>
            )}
          </div>
        </section>
      </div>
      {reseedQuote ? (
        <ReseedPosteriorModal
          quote={reseedQuote}
          onClose={closeReseedModal}
          confirm={confirm}
          alert={alert}
          actedBy={gatedBy}
        />
      ) : null}
      <ModalLayer />
    </div>
  );
}
